import { DomainEventType, PurchaseStatus } from '../enums';

/** Monetization snapshot for a creator. Derived from purchase events only. */
export interface CreatorEarnings {
  creatorId: string;
  totalRevenue: number;
  pendingPayouts: number;
  lifetimeValue: number;
}

export interface PurchaseEarningsInput {
  productId: string;
  creatorId: string;
  amount: number;
  status: PurchaseStatus;
}

/** Domain events that can affect creator earnings (implementation deferred). */
export const EARNINGS_SOURCE_EVENTS = [DomainEventType.PURCHASE_COMPLETED] as const;

export type EarningsSourceEvent = (typeof EARNINGS_SOURCE_EVENTS)[number];

/** Placeholder: aggregates earnings from completed purchases only. No payout logic yet. */
export function deriveCreatorEarningsFromPurchases(
  creatorId: string,
  purchases: PurchaseEarningsInput[],
): CreatorEarnings {
  const completed = purchases.filter(
    (p) => p.creatorId === creatorId && p.status === PurchaseStatus.COMPLETED,
  );

  const totalRevenue = completed.reduce((sum, p) => sum + p.amount, 0);

  return {
    creatorId,
    totalRevenue,
    pendingPayouts: 0,
    lifetimeValue: totalRevenue,
  };
}

/** Placeholder hook: marks that earnings should be recalculated when a purchase completes. */
export function isEarningsSourceEvent(eventType: DomainEventType): eventType is EarningsSourceEvent {
  return (EARNINGS_SOURCE_EVENTS as readonly DomainEventType[]).includes(eventType);
}
